(() => {
  'use strict';

  const namespace = '__livenzaMainProduct';

  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const initializedRoots = new WeakSet();
  const initializedShippingBlocks = new WeakSet();

  const messages = {
    invalidZip: 'Digite um CEP válido com 8 números.',
    emptyCart: 'Adicione um produto à sacola para calcular o frete.',
    noRates: 'Nenhuma modalidade de frete foi encontrada para este CEP.',
    genericError: 'Não foi possível calcular o frete agora. Tente novamente.',
    loading: 'Calculando…'
  };

  const shopRoot = () => {
    const configuredRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    const root = configuredRoot || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const endpoint = (path) => new URL(`${shopRoot()}${path.replace(/^\//, '')}`, window.location.origin);

  const digitsOnly = (value) => String(value || '').replace(/\D/g, '').slice(0, 8);

  const formatZip = (value) => {
    const digits = digitsOnly(value);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  const formatMoney = (value, currency) => {
    const amount = Number.parseFloat(value);

    if (!Number.isFinite(amount)) return String(value || '');

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
        style: 'currency',
        currency: currency || 'BRL'
      }).format(amount);
    } catch (_error) {
      return `${currency || ''} ${amount.toFixed(2)}`.trim();
    }
  };

  const normalizedText = (value) => String(value || '').trim().toLocaleLowerCase(document.documentElement.lang || 'pt-BR');

  const removeDuplicatedSelectedValue = (selectedValue) => {
    const label = selectedValue.closest('[data-livenza-option-label]') || selectedValue.parentElement;
    const value = normalizedText(selectedValue.textContent);
    if (!label || !value) return;

    [...label.childNodes].forEach((node) => {
      if (node === selectedValue) return;

      if (node.nodeType === Node.TEXT_NODE && normalizedText(node.textContent) === value) {
        node.remove();
        return;
      }

      if (
        node.nodeType === Node.ELEMENT_NODE &&
        !node.matches('modal-opener, modal-dialog, .product-popup-modal__opener') &&
        normalizedText(node.textContent) === value
      ) {
        node.remove();
      }
    });
  };

  const syncSelectedValue = (scope) => {
    if (!scope) return;

    scope.querySelectorAll('[data-livenza-selected-option]').forEach((selectedValue) => {
      const option = selectedValue.closest('.product-form__input');
      if (!option) return;

      const checkedInput = option.querySelector("input[type='radio']:checked");
      const select = option.querySelector('select');
      const value = checkedInput ? checkedInput.value : select ? select.value : '';

      if (value) selectedValue.textContent = value;
      removeDuplicatedSelectedValue(selectedValue);
    });
  };

  const setResult = (block, text, isError = false) => {
    const result = block.querySelector('[data-lz-result]');
    if (!result) return;

    result.replaceChildren();
    result.classList.toggle('livenza-ship__result--error', isError);

    if (text) result.textContent = text;
  };

  const renderRates = (block, rates) => {
    const result = block.querySelector('[data-lz-result]');
    if (!result) return;

    result.replaceChildren();
    result.classList.remove('livenza-ship__result--error');

    if (block.dataset.successMessage) {
      const message = document.createElement('p');
      message.className = 'livenza-ship__success';
      message.textContent = block.dataset.successMessage;
      result.append(message);
    }

    const list = document.createElement('ul');
    list.className = 'livenza-ship__rates';
    const currency = block.dataset.currency || 'BRL';

    rates.forEach((rate) => {
      const item = document.createElement('li');
      const title = document.createElement('span');
      const price = document.createElement('strong');

      item.className = 'livenza-ship__rate';
      title.textContent = rate.name || rate.title || 'Frete';
      price.textContent = formatMoney(rate.price, currency);
      item.append(title, price);
      list.append(item);
    });

    result.append(list);
  };

  const readJson = async (response) => {
    const type = response.headers.get('content-type') || '';
    if (!type.includes('application/json')) return null;

    try {
      return await response.json();
    } catch (_error) {
      return null;
    }
  };

  const calculateShipping = async (block) => {
    const input = block.querySelector('[data-lz-zip]');
    const button = block.querySelector('[data-lz-calc]');
    if (!input || !button) return;

    const zip = digitsOnly(input.value);
    input.value = formatZip(zip);

    if (zip.length !== 8) {
      setResult(block, block.dataset.invalidMessage || messages.invalidZip, true);
      input.focus();
      return;
    }

    if (block._livenzaShippingController) block._livenzaShippingController.abort();
    const controller = new AbortController();
    block._livenzaShippingController = controller;

    const originalLabel = button.dataset.originalLabel || button.textContent.trim();
    button.dataset.originalLabel = originalLabel;
    button.disabled = true;
    button.textContent = block.dataset.loadingLabel || messages.loading;
    block.setAttribute('aria-busy', 'true');
    setResult(block, '');

    try {
      const cartResponse = await fetch(endpoint('cart.js'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      const cart = await readJson(cartResponse);

      if (!cartResponse.ok || !cart) throw new Error('cart');

      if (!cart.item_count) {
        setResult(block, block.dataset.emptyMessage || messages.emptyCart);
        return;
      }

      const ratesUrl = endpoint('cart/shipping_rates.json');
      ratesUrl.searchParams.set('shipping_address[zip]', zip);
      ratesUrl.searchParams.set('shipping_address[country]', block.dataset.countryCode || 'BR');

      const ratesResponse = await fetch(ratesUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      const payload = await readJson(ratesResponse);

      if (!ratesResponse.ok || !payload) throw new Error('rates');

      const rates = Array.isArray(payload.shipping_rates) ? payload.shipping_rates : [];

      if (!rates.length) {
        setResult(block, block.dataset.noRatesMessage || messages.noRates);
        return;
      }

      renderRates(block, rates);
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      setResult(block, block.dataset.errorMessage || messages.genericError, true);
    } finally {
      if (block._livenzaShippingController === controller) {
        button.disabled = false;
        button.textContent = originalLabel;
        block.removeAttribute('aria-busy');
      }
    }
  };

  const initializeShipping = (root) => {
    root.querySelectorAll('[data-livenza-shipping]').forEach((block) => {
      if (initializedShippingBlocks.has(block)) return;
      initializedShippingBlocks.add(block);

      const input = block.querySelector('[data-lz-zip]');
      const button = block.querySelector('[data-lz-calc]');
      if (!input || !button) return;

      input.addEventListener('input', () => {
        const cursorWasAtEnd = input.selectionStart === input.value.length;
        input.value = formatZip(input.value);
        if (cursorWasAtEnd) input.setSelectionRange(input.value.length, input.value.length);
      });

      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        calculateShipping(block);
      });

      button.addEventListener('click', () => calculateShipping(block));
    });
  };

  const playVideo = (poster) => {
    const item = poster.closest('[data-livenza-video]');
    const template = item && item.querySelector('template');
    const media = template && template.content && template.content.firstElementChild;
    if (!item || !media) return;

    const activeMedia = media.cloneNode(true);
    poster.replaceWith(activeMedia);
    item.classList.add('is-playing');

    if (activeMedia instanceof HTMLVideoElement) {
      const playPromise = activeMedia.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    }
  };

  const initRoot = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    syncSelectedValue(root);
    initializeShipping(root);

    root.addEventListener('change', (event) => {
      if (!event.target.matches("variant-radios input[type='radio'], variant-selects select")) return;
      const option = event.target.closest('.product-form__input') || root;
      syncSelectedValue(option);
      queueMicrotask(() => syncSelectedValue(option));
    });

    root.addEventListener('click', (event) => {
      const poster = event.target.closest('[data-lz-video-poster]');
      if (!poster || !root.contains(poster)) return;
      playVideo(poster);
    });
  };

  const init = (scope) => {
    if (!scope) return;

    if (scope.matches && scope.matches('.livenza-product-v2[id^="MainProduct-"]')) {
      initRoot(scope);
    }

    scope.querySelectorAll('.livenza-product-v2[id^="MainProduct-"]').forEach(initRoot);
  };

  window[namespace] = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
